import AddIcon from '@offline-mui/icons-material/Add'
import ConfirmationNumberOutlinedIcon from '@offline-mui/icons-material/ConfirmationNumberOutlined'
import ContentCopyIcon from '@offline-mui/icons-material/ContentCopy'
import EventSeatOutlinedIcon from '@offline-mui/icons-material/EventSeatOutlined'
import Inventory2OutlinedIcon from '@offline-mui/icons-material/Inventory2Outlined'
import ManageSearchOutlinedIcon from '@offline-mui/icons-material/ManageSearchOutlined'
import TimerOutlinedIcon from '@offline-mui/icons-material/TimerOutlined'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Snackbar,
  Stack,
  TablePagination,
} from '@offline-mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { fetchBookings, fetchInventory } from '../api/inventory'
import type { InventoryItinerary, InventorySearchParams } from '../api/inventory'
import { writeClipboard } from '../components/CopyJsonButton'
import FlightSearchForm from '../components/FlightSearchForm'
import type { FlightSearchValues } from '../components/FlightSearchForm'
import ItineraryResultCard from '../components/ItineraryResultCard'
import EmptyState from '../components/ui/EmptyState'
import PageHeader from '../components/ui/PageHeader'
import SectionCard from '../components/ui/SectionCard'
import StatCard from '../components/ui/StatCard'

const formatCount = new Intl.NumberFormat().format

function toApiParams(values: FlightSearchValues): InventorySearchParams {
  return {
    departureAirport: values.departureAirport || undefined,
    arrivalAirport: values.arrivalAirport || undefined,
    departureDateFrom: values.departureDateFrom || undefined,
    departureDateTo: values.departureDateTo || undefined,
    carriers: values.carriers,
    cabins: values.cabins,
    fareType: values.fareType || undefined,
    platingCarriers: values.platingCarriers,
    flightNumber: values.flightNumber || undefined,
    classesOfService: values.classesOfService,
    brandCodes: values.brandCodes,
    fareBasisCode: values.fareBasisCode || undefined,
    lastTicketableFrom: values.lastTicketableFrom || undefined,
    lastTicketableTo: values.lastTicketableTo || undefined,
  }
}

function summarize(rows: InventoryItinerary[]) {
  const now = Date.now()
  const weekAhead = now + 7 * 24 * 60 * 60 * 1000
  let blocked = 0
  let booked = 0
  let dueSoon = 0
  let expired = 0

  for (const row of rows) {
    blocked += Number(row.total_seats_blocked) || 0
    booked += Number(row.total_seats_booked) || 0
    if (row.last_ticketable_date) {
      const deadline = new Date(row.last_ticketable_date).getTime()
      if (Number.isFinite(deadline)) {
        if (deadline < now) expired += 1
        else if (deadline <= weekAhead) dueSoon += 1
      }
    }
  }

  return {
    blocked,
    booked,
    dueSoon,
    expired,
    utilization: blocked > 0 ? Math.round((booked / blocked) * 100) : 0,
  }
}

function ResultsSkeleton() {
  return (
    <Stack divider={<Divider />}>
      {[0, 1, 2, 3].map((index) => (
        <Stack
          key={index}
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', px: { xs: 2, md: 2.5 }, py: 2 }}
        >
          <Skeleton variant="rounded" width={42} height={30} />
          <Skeleton width={90} height={28} />
          <Skeleton width="18%" height={22} />
          <Skeleton width="18%" height={22} />
          <Skeleton width="14%" height={22} sx={{ display: { xs: 'none', md: 'block' } }} />
          <Box sx={{ flexGrow: 1 }} />
          <Skeleton width={120} height={22} />
        </Stack>
      ))}
    </Stack>
  )
}

export default function DashboardPage() {
  const [rows, setRows] = useState<InventoryItinerary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const load = useCallback(
    async (params: InventorySearchParams = {}, signal?: AbortSignal) => {
      setLoading(true)
      setError(null)
      setPage(0)
      try {
        const response = await fetchInventory(params, signal)
        let nextRows = response.data
        if (nextRows.some((row) => row.booking_count == null)) {
          const bookings = await fetchBookings({}, signal)
          const counts = new Map<number, number>()
          for (const booking of bookings.data) {
            if (booking.itinerary_id == null) continue
            counts.set(booking.itinerary_id, (counts.get(booking.itinerary_id) || 0) + 1)
          }
          nextRows = nextRows.map((row) => ({
            ...row,
            booking_count: counts.get(row.id) || 0,
          }))
        }
        setRows(nextRows)
      } catch (err) {
        // An aborted request (StrictMode remount, or a newer search) is not an error.
        if (signal?.aborted) return
        setError(err instanceof Error ? err.message : 'Failed to load inventory')
        setRows([])
      } finally {
        if (!signal?.aborted) setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load({}, controller.signal)
    return () => controller.abort()
  }, [load])

  const handleCopyAll = async () => {
    await writeClipboard(JSON.stringify({ count: rows.length, data: rows }, null, 2))
    setCopyMessage(`Copied ${rows.length} blocks to clipboard`)
  }

  const stats = useMemo(() => summarize(rows), [rows])
  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <>
      <PageHeader
        eyebrow="Inventory"
        title="Flight inventory search"
        description="Browse every block by default, then narrow the list with the filters. Hover a price to see the fare breakdown."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => void handleCopyAll()}
              disabled={!rows.length || loading}
            >
              Copy all data
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/offline-flight/create"
            >
              New block
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        <StatCard
          label="Blocks loaded"
          value={formatCount(rows.length)}
          hint="Matching the current filters"
          icon={<Inventory2OutlinedIcon />}
          tone="primary"
          loading={loading}
        />
        <StatCard
          label="Seats blocked"
          value={formatCount(stats.blocked)}
          hint="Across all loaded blocks"
          icon={<EventSeatOutlinedIcon />}
          tone="info"
          loading={loading}
        />
        <StatCard
          label="Seats booked"
          value={formatCount(stats.booked)}
          hint={`${stats.utilization}% of blocked seats`}
          icon={<ConfirmationNumberOutlinedIcon />}
          tone="success"
          loading={loading}
        />
        <StatCard
          label="Ticketing due in 7 days"
          value={formatCount(stats.dueSoon)}
          hint={
            stats.expired > 0
              ? `${formatCount(stats.expired)} past deadline`
              : 'None past deadline'
          }
          icon={<TimerOutlinedIcon />}
          tone="warning"
          loading={loading}
        />
      </Box>

      <Stack spacing={3}>
        <FlightSearchForm
          onSearch={(values) => void load(toApiParams(values))}
          onReset={() => void load()}
        />

        {error ? <Alert severity="error">{error}</Alert> : null}

        <SectionCard
          title="Results"
          subtitle="Expand a block to see each direction and its flight details."
          disablePadding
          action={
            <Chip
              size="small"
              color="primary"
              label={loading ? 'Loading…' : `${formatCount(rows.length)} blocks`}
            />
          }
        >
          {loading ? (
            <ResultsSkeleton />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ManageSearchOutlinedIcon />}
              title="No blocks matched"
              description="Try widening the date range or clearing a carrier or cabin filter."
            />
          ) : (
            <>
              {pagedRows.map((row) => (
                <ItineraryResultCard
                  key={row.id}
                  itinerary={row}
                  onCloned={(clone) => {
                    setRows((current) => [clone, ...current])
                    setPage(0)
                    setCopyMessage(`Cloned as block #${clone.id}`)
                  }}
                  onDeleted={(id) => {
                    setRows((current) => current.filter((item) => item.id !== id))
                    setCopyMessage(`Deleted block #${id}`)
                  }}
                />
              ))}
              <Divider />
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </SectionCard>
      </Stack>

      <Snackbar
        open={Boolean(copyMessage)}
        autoHideDuration={2200}
        onClose={() => setCopyMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="standard" onClose={() => setCopyMessage(null)}>
          {copyMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
